import React, { useRef, useEffect } from 'react';
import { Bold, List, Star, ShieldCheck, Sparkles, Lightbulb } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';

const DescriptionToolbarTextarea = ({
  value,
  onChange,
  itemName = '',
  isService = false,
  placeholder
}) => {
  const { t } = useLang();
  const textareaRef = useRef(null);

  // Auto adjust textarea height so all text lines are clearly visible without inner scroll clipping
  const adjustHeight = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      const targetHeight = Math.max(140, el.scrollHeight);
      el.style.height = `${targetHeight + 6}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
    const t1 = setTimeout(adjustHeight, 50);
    const t2 = setTimeout(adjustHeight, 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [value]);

  const insertFormattingTag = (prefix, suffix) => {
    const el = textareaRef.current;
    const currentText = value || '';
    if (!el) {
      onChange(currentText + prefix + suffix);
      return;
    }

    const start = el.selectionStart;
    const end = el.selectionEnd;

    if (start !== undefined && end !== undefined && start !== end) {
      const selected = currentText.substring(start, end);
      const replacement = `${prefix}${selected}${suffix}`;
      const newText = currentText.substring(0, start) + replacement + currentText.substring(end);
      onChange(newText);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
        adjustHeight();
      }, 0);
    } else {
      const insertPos = (start !== undefined && start >= 0) ? start : currentText.length;
      const newText = currentText.substring(0, insertPos) + `${prefix}${suffix}` + currentText.substring(insertPos);
      onChange(newText);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(insertPos + prefix.length, insertPos + prefix.length);
        adjustHeight();
      }, 0);
    }
  };

  const insertTemplate = () => {
    const name = (itemName || '').trim();
    const title = name.length > 0
      ? name
      : (isService ? (t(LangKeys.service_name) || 'Service Name') : (t(LangKeys.item_name) || 'Product Name'));

    let template = '';
    if (isService) {
      template = `*${title}*

*⭐ मुख्य विशेषताएं (Key Features):*
• अनुभवी व प्रशिक्षित स्टाफ द्वारा काम
• ओरिजिनल स्पेयर पार्ट्स व उचित दर
• होम सर्विस व त्वरित डिलीवरी उपलब्ध

*🛠️ गारंटी व सेवा विवरण:*
• 100% संतुष्टि और 30 दिन की सर्विस गारंटी`;
    } else {
      template = `*${title}*

*⭐ मुख्य खासियतें (Key Highlights):*
• 100% प्रीमियम व ओरिजिनल क्वालिटी
• आसान उपयोग व बेस्ट परफॉरमेंस
• फास्ट व सुरक्षित पैकेजिंग

*📦 वारंटी व विवरण:*
• 1 साल की कंपनी वारंटी उपलब्ध`;
    }

    if (!value || value.trim().length === 0) {
      onChange(template);
    } else {
      onChange(`${value.trim()}\n\n${template}`);
    }

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        adjustHeight();
      }
    }, 0);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Formatting Tools Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          overflowX: 'auto',
          paddingBottom: '4px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Bold */}
        <button
          type="button"
          onClick={() => insertFormattingTag('*', '*')}
          style={styles.chip}
          title="Bold text"
        >
          <Bold size={13} strokeWidth={2.8} />
          <span>{t(LangKeys.market_format_bold) || 'Bold'}</span>
        </button>

        {/* Bullet List */}
        <button
          type="button"
          onClick={() => insertFormattingTag('\n• ', '')}
          style={styles.chip}
          title="Bullet list"
        >
          <List size={13} strokeWidth={2.4} />
          <span>{t(LangKeys.market_format_bullet) || 'Bullet'}</span>
        </button>

        {/* Highlight / Star */}
        <button
          type="button"
          onClick={() => insertFormattingTag('\n*⭐ खासियत:* ', '')}
          style={styles.chip}
          title="Highlight features"
        >
          <Star size={13} fill="#EAB308" color="#CA8A04" />
          <span>{t(LangKeys.market_format_highlight) || 'Highlight'}</span>
        </button>

        {/* Warranty */}
        <button
          type="button"
          onClick={() => insertFormattingTag(isService ? '\n*🛠️ गारंटी:* ' : '\n*📦 वारंटी:* ', '')}
          style={styles.chip}
          title="Warranty & Guarantee"
        >
          <ShieldCheck size={13} color="#16A34A" />
          <span>
            {isService
              ? (t(LangKeys.market_format_warranty_service) || 'Guarantee')
              : (t(LangKeys.market_format_warranty_item) || 'Warranty')}
          </span>
        </button>

        {/* Auto Template */}
        <button
          type="button"
          onClick={insertTemplate}
          style={{ ...styles.chip, ...styles.templateChip }}
          title="Insert sample template"
        >
          <Sparkles size={13} color="#D97706" />
          <span>
            {isService
              ? (t(LangKeys.market_format_template_service) || 'Template')
              : (t(LangKeys.market_format_template_item) || 'Template')}
          </span>
        </button>
      </div>

      {/* Auto-expanding Textarea Box: Expands with content so all written lines are visible */}
      <textarea
        ref={textareaRef}
        style={styles.textarea}
        placeholder={placeholder || (isService
          ? (t(LangKeys.service_desc_hint) || 'Describe your expertise, timings, home visit or specialty...')
          : (t(LangKeys.item_desc_hint) || 'Describe product features, brand, size or specialty...'))}
        value={value || ''}
        onInput={adjustHeight}
        onChange={e => {
          onChange(e.target.value);
          adjustHeight();
        }}
        rows={5}
      />

      {/* SEO Tip */}
      <div style={styles.tipBox}>
        <Lightbulb size={13} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
        <span style={styles.tipText}>
          {isService
            ? (t(LangKeys.market_seo_tip_service) || 'Tip: Mention your experience, service areas and timings to get direct customer inquiries on WhatsApp/Call.')
            : (t(LangKeys.market_seo_tip_item) || 'Tip: Adding brand, model, size and features helps your product rank higher in Google Search and Marketplace.')}
        </span>
      </div>
    </div>
  );
};

const styles = {
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    color: '#334155',
    fontSize: '11.5px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
  },
  templateChip: {
    backgroundColor: '#FEF3C7',
    borderColor: '#FDE68A',
    color: '#92400E'
  },
  textarea: {
    width: '100%',
    minHeight: '140px',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1.5px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    fontSize: '13.5px',
    lineHeight: '1.6',
    color: '#0F172A',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'none',
    overflowY: 'hidden',
    overflowX: 'hidden',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)'
  },
  tipBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    marginTop: '2px',
    padding: '2px 4px'
  },
  tipText: {
    fontSize: '11px',
    color: '#64748B',
    lineHeight: '1.4',
    fontWeight: '500'
  }
};

export default DescriptionToolbarTextarea;
